import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { StatusBanner } from '../../components/StatusBanner';
import { useLocale } from '../../i18n/LocaleProvider';
import { isShopAccessRequired, opsErrorMessage } from '../../lib/ops-api';
import {
  opsErrorCode,
  previewReissue,
  reissueInvoice,
  type OpsDocumentReissueResult,
  type OpsDocumentRow,
} from '../../lib/ops-documents-api';
import {
  errorKeyForCode,
  normaliseVatId,
  reissueBody,
  reissueProblem,
  reissueRefusalKey,
} from '../../lib/ops-documents';
import { REASON_MIN_LENGTH } from '../../lib/shop-file';

/**
 * The reissue form, then its confirm step: a Storno for the invoice plus a new invoice from the
 * order's current data, with the customer's VAT ID captured first when there is one.
 *
 * The preview is read as soon as the form opens — the numbers the pair is EXPECTED to draw and
 * the refusal the API would answer with — so the operator sees "RE-2701 / RE-2702" and any
 * "already superseded" before typing a reason. The numbers are not reserved: the result banner
 * reports what was actually drawn.
 *
 * The VAT ID follows the merchant's own capture: it needs the merchant's confirmation that the
 * CUSTOMER supplied it (the ticket the reason names), it is format-checked server-side, and it is
 * never validated against VIES — the form says so rather than implying a verified number.
 */
export function ReissueForm({
  row,
  shopId,
  onCancel,
  onDone,
  onAccessRequired,
}: {
  readonly row: OpsDocumentRow;
  readonly shopId: string;
  readonly onCancel: () => void;
  readonly onDone: (data: OpsDocumentReissueResult) => void;
  readonly onAccessRequired: () => void;
}) {
  const { t, tCount } = useLocale();

  const [reason, setReason] = useState('');
  const [vatId, setVatId] = useState('');
  const [customerSupplied, setCustomerSupplied] = useState(false);
  const [standing, setStanding] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const preview = useQuery({
    queryKey: ['ops-reissue-preview', shopId, row.id],
    queryFn: () => previewReissue(shopId, row.id),
    retry: false,
  });
  useEffect(() => {
    if (preview.isError && isShopAccessRequired(preview.error, shopId)) {
      onAccessRequired();
    }
  }, [preview.isError, preview.error, shopId, onAccessRequired]);
  const refusalKey = reissueRefusalKey(preview.data?.refusal);

  const form = {
    reason,
    customerVatId: vatId,
    customerSuppliedVatId: customerSupplied,
    standingDeclaration: standing,
  };
  const problem = reissueProblem(form);
  const typedVatId = normaliseVatId(vatId);

  const submit = useMutation({
    mutationFn: () => reissueInvoice(shopId, row.id, reissueBody(form)),
    onSuccess: (data) => onDone(data),
    onError: (error) => {
      setConfirming(false);
      if (isShopAccessRequired(error, shopId)) onAccessRequired();
    },
  });

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    if (problem || refusalKey) return;
    setConfirming(true);
  }

  const submitErrorKey = submit.isError
    ? errorKeyForCode(opsErrorCode(submit.error))
    : null;

  return (
    <section className="card space-y-3">
      <h2 className="text-base font-semibold text-gray-900">
        {t('opsDocuments.form.reissueTitle', { number: row.number })}
      </h2>
      <p className="text-sm text-gray-600">
        {t('opsDocuments.form.reissueLead')}
      </p>

      {preview.isLoading ? (
        <StatusBanner tone="loading">
          {t('opsDocuments.form.reissuePreviewLoading')}
        </StatusBanner>
      ) : preview.isError ? (
        <StatusBanner tone="error">
          {opsErrorMessage(
            preview.error,
            t('opsDocuments.form.reissuePreviewFailed'),
          )}
        </StatusBanner>
      ) : preview.data ? (
        <StatusBanner tone={refusalKey ? 'warning' : 'info'}>
          <div className="space-y-1">
            {refusalKey && <p>{t(refusalKey)}</p>}
            <p>
              {t('opsDocuments.form.reissueExpectedNumbers', {
                storno: preview.data.expectedStornoNumber,
                invoice: preview.data.expectedInvoiceNumber,
              })}
            </p>
            {preview.data.recentCorrections > 0 && (
              <p>
                {tCount(
                  'opsDocuments.form.reissueRecentCorrections',
                  preview.data.recentCorrections,
                )}
              </p>
            )}
          </div>
        </StatusBanner>
      ) : null}

      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <label htmlFor="opsReissueVatId" className="field-label">
            {t('opsDocuments.form.vatIdLabel')}
          </label>
          <input
            id="opsReissueVatId"
            value={vatId}
            onChange={(event) => setVatId(event.target.value)}
            maxLength={32}
            className="field-input font-mono"
            autoComplete="off"
            spellCheck={false}
          />
          <p className="field-hint">{t('opsDocuments.form.vatIdHint')}</p>
        </div>
        {typedVatId !== '' && (
          <div className="space-y-2">
            <label className="flex items-start gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                checked={customerSupplied}
                onChange={(event) => setCustomerSupplied(event.target.checked)}
                className="mt-0.5"
              />
              <span>{t('opsDocuments.form.vatIdSuppliedLabel')}</span>
            </label>
            <label className="flex items-start gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                checked={standing}
                onChange={(event) => setStanding(event.target.checked)}
                className="mt-0.5"
              />
              <span>{t('opsDocuments.form.vatIdStandingLabel')}</span>
            </label>
          </div>
        )}

        <div>
          <label htmlFor="opsReissueReason" className="field-label">
            {t('opsDocuments.form.reasonLabel')}
          </label>
          <textarea
            id="opsReissueReason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={3}
            maxLength={500}
            className="field-input"
          />
          <p className="field-hint">
            {t('opsDocuments.form.reasonHint', { min: REASON_MIN_LENGTH })}
          </p>
        </div>

        {problem && (reason.trim() !== '' || typedVatId !== '') && (
          <p className="text-sm text-amber-700">{t(problem)}</p>
        )}
        {submit.isError && (
          <StatusBanner tone="error">
            {submitErrorKey
              ? t(submitErrorKey)
              : opsErrorMessage(
                  submit.error,
                  t('opsDocuments.errors.reissueFailed'),
                )}
          </StatusBanner>
        )}

        <div className="flex gap-2">
          <button
            type="submit"
            className="btn-primary btn-sm"
            disabled={
              problem !== null ||
              refusalKey !== null ||
              !preview.data ||
              submit.isPending
            }
          >
            {t('opsDocuments.form.continue')}
          </button>
          <button
            type="button"
            className="btn-neutral btn-sm"
            onClick={onCancel}
          >
            {t('common.cancel')}
          </button>
        </div>
      </form>

      <ConfirmDialog
        open={confirming}
        title={t('opsDocuments.confirm.reissueTitle')}
        confirmLabel={t('opsDocuments.confirm.reissueLabel')}
        confirmDisabled={submit.isPending}
        onConfirm={() => submit.mutate()}
        onCancel={() => setConfirming(false)}
      >
        <div className="space-y-2">
          <p>
            {t('opsDocuments.confirm.reissueBody', {
              number: row.number,
              storno: preview.data?.expectedStornoNumber ?? '—',
              invoice: preview.data?.expectedInvoiceNumber ?? '—',
            })}
          </p>
          {typedVatId !== '' && (
            <p>
              {t('opsDocuments.confirm.reissueVatId', { vatId: typedVatId })}
            </p>
          )}
          <p>{t('opsDocuments.confirm.reissueDelivery')}</p>
        </div>
      </ConfirmDialog>
    </section>
  );
}
